import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PLAYER_DECK_PRODUCT_CARD_LINK, PLAYER_DECK_PRODUCT_CARD_PRICE_WRAPPER, } from '@core/components/testIds';
import { EventType } from '@core/customEvent';
import { isTruthy } from '@core/helpers/boolean';
import { useVisibilityObserver } from '@core/hooks/useVisibilityObserver';
import { useContextTranslation } from '@core/i18n';
import { logInfoCollapsed } from '@core/logging/helpers';
import { useContextParams } from '@core/params/context';
import { usePIPTarget } from '@core/pictureInPicture/usePIPTarget';
import { ProductCardImageFallback } from '@core/shopping/components/ProductCardCarousel/ProductCardImageFallback';
import { useProductImage } from '@core/shopping/hooks/useProductImage';
import { useProductUnitPrice } from '@core/shopping/hooks/useProductUnitPrice';
import { useQuickAdd } from '@core/shopping/hooks/useQuickAdd/useQuickAdd';
import { useSelectedOptionProductUnit } from '@core/shopping/hooks/useSelectedOptionProductUnit';
import { useShoppingApi } from '@core/shopping/hooks/useShoppingApi/useShoppingApi';
import { getProductUrl } from '@core/shopping/utils/product';
import { useTrackPlayerDeckProductCardClick } from '@core/widgets/tracking/useTrackPlayerDeckProductCardClick';
import { AddToCartButton } from '@embed/components/Grid/PlayerDeckContainer/AddToCartButton';
import { customEmbedEvent } from '@embed/customEmbedEvent';
import { ContentWrapper, CustomProductPriceStyle, DiscountPercent, OriginalPrice, Price, PriceWrapper, ProductCard, ProductCardGrid, ProductImage, ProductLink, ProductTitle, SoldOutLabel, } from './PlayerDeckContainer.styles';
const ADD_TO_CART_STATUS_TTL = 1000;
export const ADD_TO_CART_HIDDEN_REASON = {
    NOT_HYDRATED: 'product is not hydrated',
    SOLD_OUT: 'product is sold out (one or more units are not available)',
    NO_QUICK_ADD: 'quick add is not enabled',
    NO_QUICK_VIEW: 'quick view is not enabled',
};
/**
 * Product Card Item
 */
export const ProductCardItem = ({ product, onVisibilityChange, video, }) => {
    const PRODUCT_CARD_VISIBILITY_THRESHOLD = 0.95;
    const selectedOptionProductUnit = useSelectedOptionProductUnit({ product });
    const params = useContextParams();
    const { currentImageSrc, productImageSrc, altText } = useProductImage({
        product,
        unit: selectedOptionProductUnit,
    });
    const { url } = usePIPTarget({
        params,
        url: getProductUrl(product, selectedOptionProductUnit),
    });
    /**
     * Observe visibility and trigger callback
     */
    const onVisibilityObserverChange = useCallback((isVisible) => {
        onVisibilityChange?.(isVisible);
    }, [onVisibilityChange]);
    const imageRef = useRef(null);
    const cardItemRef = useVisibilityObserver({
        onChange: onVisibilityObserverChange,
        threshold: [PRODUCT_CARD_VISIBILITY_THRESHOLD],
    });
    const unit = selectedOptionProductUnit ?? product.product_units[0];
    const unitName = selectedOptionProductUnit
        ? ` (${selectedOptionProductUnit.unit_name})`
        : '';
    const currency = product.product_currency;
    const { unitPriceWithCurrency, unitOriginalPriceWithCurrency, unitPrice, unitOriginalPrice, } = useProductUnitPrice({
        unit,
        currency,
    });
    const discountPercent = useMemo(() => unitPrice && unitOriginalPrice
        ? Math.round((1 - unitPrice / unitOriginalPrice) * 100)
        : 0, [unitPrice, unitOriginalPrice]);
    const isProductPriceHidden = Boolean(product.product_hide_price) && Boolean(params?.hide_product_price);
    // if product not hydrated, available is undefined
    const isProductAvailable = product.product_units.every((productUnit) => isTruthy(productUnit.available));
    // we only want to consider sold out if available has been deliberately set to false
    const isProductSoldOut = product.product_units.every((productUnit) => productUnit.available === false);
    const { addToCart, loading: shoppingApiLoading } = useShoppingApi(video);
    const [cartActionSuccess, setCartActionSuccess] = useState(undefined);
    useEffect(() => {
        // undefined is the default state
        // if it changes from that state,
        // we set a timeout to show the status
        // for a short period of time
        if (cartActionSuccess !== undefined) {
            setTimeout(() => {
                setCartActionSuccess(undefined);
            }, ADD_TO_CART_STATUS_TTL);
        }
    }, [cartActionSuccess]);
    const { trackProductCardClick, trackAddToCartClick } = useTrackPlayerDeckProductCardClick(video);
    const handleAddToCartClick = useCallback((e) => {
        e.stopPropagation();
        customEmbedEvent({
            type: EventType.FW_SHOPPING_PLAYER_DECK_ADD_TO_CART_CLICK,
            params: {},
            data: {
                product,
                product_id: product.product_id,
            },
        });
        if (product.product_units.length === 1) {
            addToCart({
                product,
                callback: (success) => {
                    setCartActionSuccess(success);
                    trackAddToCartClick(product);
                },
            });
        }
    }, [addToCart, product, setCartActionSuccess, trackAddToCartClick]);
    const handleLinkClick = useCallback((e) => {
        e.stopPropagation();
        trackProductCardClick(product);
        customEmbedEvent({
            type: EventType.FW_SHOPPING_PRODUCT_CARD_CLICKED,
            data: { product, product_id: product.product_id },
            params,
        });
    }, [trackProductCardClick, product, params]);
    const { t } = useContextTranslation();
    const { quickAdd: supportsQuickAdd, quickView: supportsQuickView } = useQuickAdd();
    let hasAddToCartButton = false;
    // product is available and in stock
    if (isProductAvailable && !isProductSoldOut) {
        // product with no variants
        if (product.product_units.length === 1) {
            hasAddToCartButton = supportsQuickAdd;
        }
        else {
            // product with multiple variants
            hasAddToCartButton = supportsQuickView;
        }
    }
    useEffect(() => {
        if (!hasAddToCartButton) {
            const reasons = [];
            if (!isProductAvailable) {
                reasons.push(ADD_TO_CART_HIDDEN_REASON.NOT_HYDRATED);
            }
            if (isProductSoldOut) {
                reasons.push(ADD_TO_CART_HIDDEN_REASON.SOLD_OUT);
            }
            if (product.product_units.length === 1 && !supportsQuickAdd) {
                reasons.push(ADD_TO_CART_HIDDEN_REASON.NO_QUICK_ADD);
            }
            if (product.product_units.length > 1 && !supportsQuickView) {
                reasons.push(ADD_TO_CART_HIDDEN_REASON.NO_QUICK_VIEW);
            }
            logInfoCollapsed(`${product.product_name} does not support Add To Cart button`, reasons);
        }
    }, [
        hasAddToCartButton,
        isProductAvailable,
        isProductSoldOut,
        product.product_name,
        product.product_units.length,
        supportsQuickAdd,
        supportsQuickView,
    ]);
    return (<ProductCardGrid>
      <ProductLink href={url} target="_self" ref={cardItemRef} onClick={handleLinkClick} withAddToCart={hasAddToCartButton} data-testid={PLAYER_DECK_PRODUCT_CARD_LINK}>
        <ProductCard>
          <ProductImage src={currentImageSrc ?? productImageSrc} alt={altText} ref={imageRef} fallbackComponent={<ProductCardImageFallback />}/>

          <ContentWrapper>
            <ProductTitle>
              {product.product_name}
              {unitName}
            </ProductTitle>
            {isProductPriceHidden ? null : isProductSoldOut ? (<SoldOutLabel>{t('player:Sold Out')}</SoldOutLabel>) : (<CustomProductPriceStyle>
                {unitOriginalPriceWithCurrency ? (<PriceWrapper data-testid={PLAYER_DECK_PRODUCT_CARD_PRICE_WRAPPER}>
                    <Price>{unitPriceWithCurrency}</Price>
                    {discountPercent ? (<DiscountPercent>-{discountPercent}%</DiscountPercent>) : null}
                    <OriginalPrice>
                      {unitOriginalPriceWithCurrency}
                    </OriginalPrice>
                  </PriceWrapper>) : unitPriceWithCurrency ? (<Price>{unitPriceWithCurrency}</Price>) : null}
              </CustomProductPriceStyle>)}
          </ContentWrapper>
        </ProductCard>
      </ProductLink>
      {hasAddToCartButton ? (<AddToCartButton status={cartActionSuccess} onAddClick={handleAddToCartClick} addButtonDisabled={shoppingApiLoading} productName={product.product_name}/>) : null}
    </ProductCardGrid>);
};
